import { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  BarChart3,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  FileEdit,
  X,
  ChevronUp,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { ErrorState, EmptyState, Skeleton } from '../../components/ui';
import { ConfirmModal } from '../../components/ConfirmModal';

interface WorkflowProgress {
  phase1: { total: number; completed: number; done: boolean };
  phase1Eval: { completed: number; done: boolean };
  phase2: { total: number; completed: number; started: boolean; done: boolean };
  phase2Eval: { completed: number; done: boolean };
  qualityAssessment?: { completed: number; done: boolean; applicable: boolean };
  workflowStatus: 'pending' | 'phase1_running' | 'phase1_eval' | 'awaiting_phase2' | 'phase2_running' | 'phase2_eval' | 'quality_assessment' | 'completed';
}

interface TestRun {
  id: string;
  name: string;
  runType: 'MODEL_COMPARISON' | 'MODEL_STABILITY';
  status: 'DRAFT' | 'PENDING' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  isRevealed: boolean;
  startedAt?: string;
  completedAt?: string;
  totalExecutions: number;
  completedExecutions: number;
  workflowProgress?: WorkflowProgress;
  createdAt: string;
  testCase: {
    id: string;
    name: string;
    code: string;
    category: string;
  };
  createdBy: { id: string; name: string };
}

type SortField = 'name' | 'status' | 'progress' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_ORDER = {
  RUNNING: 0,
  PENDING: 1,
  PAUSED: 2,
  DRAFT: 3,
  COMPLETED: 4,
  FAILED: 5,
  CANCELLED: 6,
};

function StatusBadge({ status, workflowProgress }: { status: TestRun['status']; workflowProgress?: WorkflowProgress }) {
  // Use workflow status if available, otherwise fall back to database status
  const workflowStatus = workflowProgress?.workflowStatus;

  const workflowConfig: Record<string, { icon: React.ElementType; color: string; label: string; dot?: string }> = {
    pending: { icon: Clock, color: 'bg-gray-100 text-gray-600', label: 'Pending', dot: 'bg-gray-400' },
    phase1_running: { icon: Play, color: 'bg-blue-100 text-blue-700', label: 'P2+3 Running', dot: 'bg-blue-400 animate-pulse' },
    phase1_eval: { icon: Eye, color: 'bg-amber-100 text-amber-700', label: 'Awaiting E2+3', dot: 'bg-amber-400' },
    awaiting_phase2: { icon: Play, color: 'bg-purple-100 text-purple-700', label: 'Ready for P4', dot: 'bg-purple-400' },
    phase2_running: { icon: Play, color: 'bg-blue-100 text-blue-700', label: 'P4 Running', dot: 'bg-blue-400 animate-pulse' },
    phase2_eval: { icon: Eye, color: 'bg-amber-100 text-amber-700', label: 'Awaiting E4', dot: 'bg-amber-400' },
    quality_assessment: { icon: Eye, color: 'bg-amber-100 text-amber-700', label: 'Awaiting E5', dot: 'bg-amber-400' },
    completed: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Completed', dot: 'bg-green-400' },
  };

  const dbStatusConfig: Record<string, { icon: React.ElementType; color: string; label: string; dot?: string }> = {
    DRAFT: { icon: FileEdit, color: 'bg-purple-100 text-purple-700', label: 'Draft', dot: 'bg-purple-400' },
    PENDING: { icon: Clock, color: 'bg-gray-100 text-gray-600', label: 'Pending', dot: 'bg-gray-400' },
    RUNNING: { icon: Play, color: 'bg-blue-100 text-blue-700', label: 'Running', dot: 'bg-blue-400 animate-pulse' },
    PAUSED: { icon: Pause, color: 'bg-amber-100 text-amber-700', label: 'Paused', dot: 'bg-amber-400' },
    COMPLETED: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Completed', dot: 'bg-green-400' },
    FAILED: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Failed', dot: 'bg-red-400' },
    CANCELLED: { icon: XCircle, color: 'bg-gray-100 text-gray-500', label: 'Cancelled', dot: 'bg-gray-400' },
  };

  // For DRAFT, FAILED, CANCELLED, PAUSED - use database status
  // For others, prefer workflow status if available
  let cfg: { icon: React.ElementType; color: string; label: string; dot?: string };
  if (['DRAFT', 'FAILED', 'CANCELLED', 'PAUSED'].includes(status)) {
    cfg = dbStatusConfig[status];
  } else if (workflowStatus && workflowConfig[workflowStatus]) {
    cfg = workflowConfig[workflowStatus];
  } else {
    cfg = dbStatusConfig[status] || { icon: Clock, color: 'bg-gray-100 text-gray-600', label: status, dot: 'bg-gray-400' };
  }

  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', cfg.color)}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

function EnhancedProgressBar({ workflowProgress, runType }: { workflowProgress?: WorkflowProgress; runType?: 'MODEL_COMPARISON' | 'MODEL_STABILITY' }) {
  if (!workflowProgress) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  const { phase1, phase1Eval, phase2, phase2Eval, qualityAssessment, workflowStatus } = workflowProgress;
  const isComparison = runType === 'MODEL_COMPARISON';

  // Build phases array based on run type
  // MODEL_COMPARISON: P2+3 → E2+3 → P4 → E4 → E5 (5 phases)
  // MODEL_STABILITY: P2+3 → E2+3 → P4 → E4 (4 phases, no E5)
  const phases = [
    { done: phase1.done, label: 'P2+3' },
    { done: phase1Eval.done, label: 'E2+3' },
    { done: phase2.done && phase2.started, label: 'P4', pending: !phase2.started },
    { done: phase2Eval.done && phase2.started, label: 'E4', pending: !phase2.started },
  ];

  // Add E5 (Quality Assessment) only for MODEL_COMPARISON
  if (isComparison) {
    phases.push({
      done: qualityAssessment?.done ?? false,
      label: 'E5',
      pending: !phase2Eval.done || !phase2.started,
    });
  }

  // Calculate percentage based on total phases
  const totalPhases = phases.length;
  const completedPhasesCount = phases.filter(p => p.done).length;
  const percentage = Math.round((completedPhasesCount / totalPhases) * 100);

  const isComplete = workflowStatus === 'completed';
  const isRunning = ['phase1_running', 'phase2_running'].includes(workflowStatus);

  // Current phase details with new terminology
  let currentPhaseText = '';
  if (workflowStatus === 'phase1_running') {
    currentPhaseText = `P2+3: ${phase1.completed}/${phase1.total}`;
  } else if (workflowStatus === 'phase1_eval') {
    currentPhaseText = `E2+3: ${phase1Eval.completed} done`;
  } else if (workflowStatus === 'awaiting_phase2') {
    currentPhaseText = 'Ready for P4';
  } else if (workflowStatus === 'phase2_running') {
    currentPhaseText = `P4: ${phase2.completed}/${phase2.total}`;
  } else if (workflowStatus === 'phase2_eval') {
    currentPhaseText = `E4: ${phase2Eval.completed} done`;
  } else if (workflowStatus === 'quality_assessment') {
    currentPhaseText = `E5: ${qualityAssessment?.completed ?? 0} done`;
  } else if (isComplete) {
    currentPhaseText = 'All phases complete';
  }

  return (
    <div className="w-48">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={clsx(
          'font-semibold',
          isComplete ? 'text-green-600' : 'text-gray-700'
        )}>
          {percentage}%
        </span>
        <span className="text-gray-500 truncate max-w-[120px]" title={currentPhaseText}>
          {currentPhaseText}
        </span>
      </div>
      {/* Multi-phase progress bar */}
      <div className="flex gap-0.5 h-2">
        {phases.map((phase, i) => (
          <div
            key={i}
            className={clsx(
              'flex-1 rounded-sm transition-all',
              phase.done ? 'bg-green-500' :
              i === phases.findIndex(p => !p.done) && isRunning ? 'bg-blue-500 animate-pulse' :
              i === phases.findIndex(p => !p.done) && !phase.pending ? 'bg-amber-400' :
              'bg-gray-200'
            )}
            title={`${phase.label}: ${phase.done ? 'Done' : phase.pending ? 'Not Started' : 'Pending'}`}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
        {phases.map((phase, i) => (
          <span key={i}>{phase.label}</span>
        ))}
      </div>
    </div>
  );
}

function TestRunListSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left"><Skeleton width={80} height={12} /></th>
            <th className="px-4 py-3 text-left"><Skeleton width={60} height={12} /></th>
            <th className="px-4 py-3 text-left"><Skeleton width={50} height={12} /></th>
            <th className="px-4 py-3 text-left"><Skeleton width={60} height={12} /></th>
            <th className="px-4 py-3 text-left"><Skeleton width={60} height={12} /></th>
            <th className="px-4 py-3 text-left"><Skeleton width={60} height={12} /></th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {Array.from({ length: 8 }).map((_, i) => (
            <tr key={i}>
              <td className="px-4 py-3">
                <div className="space-y-1">
                  <Skeleton width="80%" height={14} />
                  <Skeleton width="50%" height={12} />
                </div>
              </td>
              <td className="px-4 py-3"><Skeleton width={120} height={14} /></td>
              <td className="px-4 py-3"><Skeleton width={80} height={24} variant="rectangular" /></td>
              <td className="px-4 py-3"><Skeleton width={160} height={24} variant="rectangular" /></td>
              <td className="px-4 py-3"><Skeleton width={70} height={20} /></td>
              <td className="px-4 py-3"><Skeleton width={80} height={14} /></td>
              <td className="px-4 py-3"><Skeleton width={100} height={24} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  sortKey: SortField;
  currentSort: { field: SortField; direction: SortDirection } | null;
  onSort: (field: SortField) => void;
}

function SortableHeader({ label, sortKey, currentSort, onSort }: SortableHeaderProps) {
  const isActive = currentSort?.field === sortKey;
  const direction = isActive ? currentSort.direction : null;

  return (
    <th
      className="px-4 py-3 text-left cursor-pointer select-none hover:bg-gray-100 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
        <div className="flex flex-col -space-y-1">
          <ChevronUp
            className={clsx(
              'h-3 w-3',
              direction === 'asc' ? 'text-amber-600' : 'text-gray-300'
            )}
          />
          <ChevronDown
            className={clsx(
              'h-3 w-3',
              direction === 'desc' ? 'text-amber-600' : 'text-gray-300'
            )}
          />
        </div>
      </div>
    </th>
  );
}

export function TestRunList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [deleteConfirm, setDeleteConfirm] = useState<TestRun | null>(null);
  const [sort, setSort] = useState<{ field: SortField; direction: SortDirection } | null>(null);
  const [groupByStatus, setGroupByStatus] = useState(true);

  const status = searchParams.get('status') || '';
  const revealed = searchParams.get('revealed') || '';
  const runType = searchParams.get('runType') || '';

  const activeFilterCount = [status, revealed, runType].filter(Boolean).length;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/validation/test-runs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] });
      setDeleteConfirm(null);
    },
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['validation', 'test-runs', { search: searchParams.get('search'), status, revealed, runType }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.set('search', searchParams.get('search')!);
      if (status) params.set('status', status);
      if (revealed === 'true') params.set('isRevealed', 'true');
      if (revealed === 'false') params.set('isRevealed', 'false');
      params.set('limit', '1000');
      return api.get<{ data: TestRun[]; pagination: { total: number } }>(
        `/api/validation/test-runs?${params.toString()}`
      );
    },
  });

  // Filter by runType client-side (API may not support this filter)
  const testRuns = useMemo(() => {
    const runs = data?.data || [];
    if (!runType) return runs;
    return runs.filter(run => run.runType === runType);
  }, [data?.data, runType]);
  const total = data?.pagination?.total || 0;

  // Sort and group test runs
  const sortedTestRuns = useMemo(() => {
    let sorted = [...testRuns];

    if (sort) {
      sorted.sort((a, b) => {
        let comparison = 0;
        switch (sort.field) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'status':
            comparison = (STATUS_ORDER[a.status] || 99) - (STATUS_ORDER[b.status] || 99);
            break;
          case 'progress': {
            const progressA = a.totalExecutions > 0 ? a.completedExecutions / a.totalExecutions : 0;
            const progressB = b.totalExecutions > 0 ? b.completedExecutions / b.totalExecutions : 0;
            comparison = progressA - progressB;
            break;
          }
          case 'createdAt':
            comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            break;
        }
        return sort.direction === 'asc' ? comparison : -comparison;
      });
    } else if (groupByStatus) {
      // Default: sort by status priority
      sorted.sort((a, b) => (STATUS_ORDER[a.status] || 99) - (STATUS_ORDER[b.status] || 99));
    }

    return sorted;
  }, [testRuns, sort, groupByStatus]);

  // Group runs by workflow status
  const groupedRuns = useMemo(() => {
    if (!groupByStatus || sort) return null;

    const activeRuns = sortedTestRuns.filter(r => {
      const ws = r.workflowProgress?.workflowStatus;
      return ['RUNNING', 'PENDING', 'PAUSED'].includes(r.status) ||
             ['phase1_running', 'phase2_running'].includes(ws || '');
    });
    const awaitingActionRuns = sortedTestRuns.filter(r => {
      const ws = r.workflowProgress?.workflowStatus;
      return ['phase1_eval', 'phase2_eval', 'awaiting_phase2', 'quality_assessment'].includes(ws || '') &&
             !['DRAFT', 'FAILED', 'CANCELLED', 'PAUSED'].includes(r.status);
    });
    const completedRuns = sortedTestRuns.filter(r => {
      const ws = r.workflowProgress?.workflowStatus;
      return ws === 'completed';
    });
    const otherRuns = sortedTestRuns.filter(r => {
      const ws = r.workflowProgress?.workflowStatus;
      return ['DRAFT', 'FAILED', 'CANCELLED'].includes(r.status) ||
             (!['phase1_running', 'phase2_running', 'phase1_eval', 'phase2_eval', 'awaiting_phase2', 'quality_assessment', 'completed'].includes(ws || '') &&
              !['RUNNING', 'PENDING', 'PAUSED'].includes(r.status));
    });

    return { activeRuns, awaitingActionRuns, completedRuns, otherRuns };
  }, [sortedTestRuns, groupByStatus, sort]);

  const handleSort = (field: SortField) => {
    setSort((current) => {
      if (current?.field === field) {
        if (current.direction === 'asc') {
          return { field, direction: 'desc' };
        }
        return null;
      }
      return { field, direction: 'asc' };
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearAllFilters = () => {
    setSearch('');
    setSearchParams(new URLSearchParams());
    setSort(null);
  };

  const renderRow = (run: TestRun) => (
    <tr key={run.id} className="hover:bg-gray-50 transition-colors group">
      <td className="px-4 py-3">
        <Link to={`/validation/test-runs/${run.id}`} className="block">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 group-hover:text-amber-600 transition-colors">
              {run.name}
            </span>
            <span className={clsx(
              'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
              run.runType === 'MODEL_STABILITY'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-blue-100 text-blue-700'
            )}>
              {run.runType === 'MODEL_STABILITY' ? 'Stability' : 'Comparison'}
            </span>
          </div>
          <div className="text-xs text-gray-500">by {run.createdBy.name}</div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/validation/test-cases/${run.testCase.id}`}
          className="text-sm text-gray-700 hover:text-amber-600 transition-colors"
        >
          {run.testCase.name}
        </Link>
        <div className="text-xs font-mono text-gray-400">{run.testCase.code}</div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={run.status} workflowProgress={run.workflowProgress} />
      </td>
      <td className="px-4 py-3">
        <EnhancedProgressBar workflowProgress={run.workflowProgress} runType={run.runType} />
      </td>
      <td className="px-4 py-3">
        {/* Visibility is only revealed after Phase 2 completion */}
        {run.isRevealed && run.workflowProgress?.workflowStatus === 'completed' ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Eye className="h-3 w-3" />
            Revealed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <EyeOff className="h-3 w-3" />
            Blinded
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-500">
          {formatDistanceToNow(new Date(run.createdAt), { addSuffix: true })}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {run.status === 'DRAFT' && (
            <button
              onClick={() => navigate(`/validation/test-runs/${run.id}/edit`)}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="Edit test run"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {run.status === 'COMPLETED' && !run.isRevealed && (
            <Link
              to={`/validation/test-runs/${run.id}/evaluate`}
              className="rounded-lg p-1.5 text-amber-500 hover:bg-amber-50 hover:text-amber-600 transition-colors"
              title="Evaluate"
            >
              <Eye className="h-4 w-4" />
            </Link>
          )}
          {run.status === 'COMPLETED' && run.isRevealed && (
            <Link
              to={run.runType === 'MODEL_STABILITY'
                ? `/validation/test-runs/${run.id}/stability-results`
                : `/validation/test-runs/${run.id}/results`}
              className="rounded-lg p-1.5 text-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
              title="View results"
            >
              <BarChart3 className="h-4 w-4" />
            </Link>
          )}
          {['RUNNING', 'PAUSED', 'PENDING'].includes(run.status) && (
            <Link
              to={`/validation/test-runs/${run.id}`}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="View details"
            >
              <Eye className="h-4 w-4" />
            </Link>
          )}
          {run.status !== 'RUNNING' && (
            <button
              onClick={() => setDeleteConfirm(run)}
              className="rounded-lg p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
              title="Delete test run"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  const renderGroupHeader = (label: string, count: number, colorClass: string) => (
    <tr className={clsx('bg-gradient-to-r', colorClass)}>
      <td colSpan={7} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider">
        {label} ({count})
      </td>
    </tr>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test Runs</h1>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
        <TestRunListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test Runs</h1>
          </div>
        </div>
        <ErrorState
          title="Failed to load test runs"
          message="Unable to load test runs. Please check your connection and try again."
          onRetry={() => refetch()}
          variant="card"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Test Runs</h1>
          <p className="text-sm text-gray-500">
            {total} test run{total !== 1 ? 's' : ''}
            {activeFilterCount > 0 && ` (filtered)`}
          </p>
        </div>
        <Link
          to="/validation/test-runs/new"
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
        >
          <Plus className="h-4 w-4" />
          New Test Run
        </Link>
      </div>

      {/* Enhanced Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {/* Search */}
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search test runs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                const params = new URLSearchParams(searchParams);
                params.delete('search');
                setSearchParams(params);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </form>

        {/* Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <select
              value={status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                status
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              status ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          <div className="relative">
            <select
              value={revealed}
              onChange={(e) => updateFilter('revealed', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                revealed
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">Visibility</option>
              <option value="false">Blinded</option>
              <option value="true">Revealed</option>
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              revealed ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          <div className="relative">
            <select
              value={runType}
              onChange={(e) => updateFilter('runType', e.target.value)}
              className={clsx(
                'appearance-none bg-none rounded-full border py-1.5 pl-3 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                runType
                  ? runType === 'MODEL_STABILITY'
                    ? 'border-purple-300 bg-purple-50 text-purple-700'
                    : 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
              style={{ backgroundImage: 'none' }}
            >
              <option value="">All Types</option>
              <option value="MODEL_COMPARISON">Comparison</option>
              <option value="MODEL_STABILITY">Stability</option>
            </select>
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              runType
                ? runType === 'MODEL_STABILITY' ? 'text-purple-500' : 'text-blue-500'
                : 'text-gray-400'
            )} />
          </div>
        </div>

        {/* Group toggle */}
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={groupByStatus}
            onChange={(e) => setGroupByStatus(e.target.checked)}
            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          <span>Group by status</span>
        </label>

        {/* Clear filters */}
        {(activeFilterCount > 0 || search || sort) && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors"
          >
            <X className="h-4 w-4" />
            Clear all
          </button>
        )}
      </div>

      {/* Error Display */}
      {deleteMutation.isError && (
        <ErrorState
          title="Failed to delete test run"
          message={deleteMutation.error instanceof Error ? deleteMutation.error.message : 'An unexpected error occurred'}
          variant="inline"
        />
      )}

      {/* List */}
      {sortedTestRuns.length === 0 ? (
        <EmptyState
          icon={Play}
          title="No test runs found"
          description={
            searchParams.toString()
              ? 'Try adjusting your filters to find what you\'re looking for'
              : 'Create your first test run to start evaluating models'
          }
          action={
            !searchParams.toString()
              ? {
                  label: 'Create Test Run',
                  href: '/validation/test-runs/new',
                  icon: Plus,
                }
              : undefined
          }
          secondaryAction={
            searchParams.toString()
              ? {
                  label: 'Clear Filters',
                  onClick: clearAllFilters,
                  variant: 'secondary',
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader
                    label="Test Run"
                    sortKey="name"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Test Case
                  </th>
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Progress"
                    sortKey="progress"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Visibility
                  </th>
                  <SortableHeader
                    label="Created"
                    sortKey="createdAt"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groupedRuns && !sort ? (
                  <>
                    {groupedRuns.activeRuns.length > 0 && (
                      <>
                        {renderGroupHeader('Running', groupedRuns.activeRuns.length, 'from-blue-50 to-transparent text-blue-700')}
                        {groupedRuns.activeRuns.map(renderRow)}
                      </>
                    )}
                    {groupedRuns.awaitingActionRuns.length > 0 && (
                      <>
                        {renderGroupHeader('Awaiting Action', groupedRuns.awaitingActionRuns.length, 'from-amber-50 to-transparent text-amber-700')}
                        {groupedRuns.awaitingActionRuns.map(renderRow)}
                      </>
                    )}
                    {groupedRuns.completedRuns.length > 0 && (
                      <>
                        {renderGroupHeader('Completed', groupedRuns.completedRuns.length, 'from-green-50 to-transparent text-green-700')}
                        {groupedRuns.completedRuns.map(renderRow)}
                      </>
                    )}
                    {groupedRuns.otherRuns.length > 0 && (
                      <>
                        {renderGroupHeader('Other', groupedRuns.otherRuns.length, 'from-gray-50 to-transparent text-gray-600')}
                        {groupedRuns.otherRuns.map(renderRow)}
                      </>
                    )}
                  </>
                ) : (
                  sortedTestRuns.map(renderRow)
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            deleteMutation.mutate(deleteConfirm.id);
          }
        }}
        title="Delete Test Run"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This will permanently delete ${deleteConfirm?.totalExecutions || 0} execution(s) and their results. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
